import React, { useState } from 'react';
import { required } from 'react-admin';
import { DateTime } from 'luxon';
import {
  Box,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';

import SmartDateInput from '../../customFields/SmartDateInput';
import Params from '../transaction/Params';
import toStockholmTime from '../../utilities/toStockholmTime';

const dateValidation = (value: string) => {
  if (value && !DateTime.fromISO(value).isValid) {
    return 'Invalid date';
  }
  return undefined;
};

const rangeValidation = (_value: string, allValues: Params) => {
  const { from, to } = allValues;

  if (from && to) {
    if (DateTime.fromISO(to) < DateTime.fromISO(from)) {
      return 'Invalid date range';
    }
    if (toStockholmTime(to).startOf('day') > toStockholmTime(DateTime.now()).startOf('day')) {
      return '"To" date should be in the past';
    }
    if (DateTime.fromISO(from).plus({ years: 5 }) < DateTime.fromISO(to)) {
      return '"From" date should not be more than 5 years before "To" date';
    }
  }
  return undefined;
};

const selectedSx = (theme: Theme) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
});

const DateRange = ({
  setParams,
  params,
}: {
  setParams: React.Dispatch<React.SetStateAction<Params>>;
  params: Params;
}) => {
  const theme = useTheme();

  const [selectedValue, setSelectedValue] = useState(() => {
    const today = toStockholmTime(DateTime.now());
    if (toStockholmTime(params.to).startOf('day').equals(today.startOf('day'))) {
      if (toStockholmTime(params.from).startOf('day').equals(today.startOf('year'))) return 'YTD';
      const dateDiff = DateTime.fromISO(params.to).diff(DateTime.fromISO(params.from), 'months').months;
      if (dateDiff === 12) return '1Y';
      if (dateDiff === 1) return '1M';
      if (dateDiff === 3) return '3M';
    }
    return 'custom';
  });

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string,
  ) => {
    if (newValue !== null) {
      setSelectedValue(newValue);
      const today = toStockholmTime(DateTime.now());
      switch (newValue) {
        case '1M':
          setParams((previousParams) => ({
            ...previousParams,
            from: today.minus({ month: 1 }).toISODate()!,
            to: today.toISODate()!,
          }));
          break;
        case '3M':
          setParams((previousParams) => ({
            ...previousParams,
            from: today.minus({ month: 3 }).toISODate()!,
            to: today.toISODate()!,
          }));
          break;
        case 'YTD':
          setParams((previousParams) => ({
            ...previousParams,
            from: today.startOf('year').toISODate()!,
            to: today.toISODate()!,
          }));
          break;
        case 'custom':
          break;
        case '1Y':
        default:
          setParams((previousParams) => ({
            ...previousParams,
            from: today.minus({ month: 12 }).toISODate()!,
            to: today.toISODate()!,
          }));
      }
    }
  };

  return (
    <>
      <Box display="flex" height={45} bgcolor={theme.palette.grey[200]} borderRadius={1}>
        <ToggleButtonGroup
          value={selectedValue}
          onChange={handleChange}
          exclusive
        >
          <ToggleButton
            value="1M"
            sx={selectedSx(theme)}
          >
            1M
          </ToggleButton>
          <ToggleButton
            value="3M"
            sx={selectedSx(theme)}
          >
            3M
          </ToggleButton>
          <ToggleButton
            value="1Y"
            sx={selectedSx(theme)}
          >
            1Y
          </ToggleButton>
          <ToggleButton
            value="YTD"
            sx={selectedSx(theme)}
          >
            YTD
          </ToggleButton>
          <ToggleButton
            value="custom"
            sx={selectedSx(theme)}
          >
            <CalendarMonth />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {selectedValue === 'custom' && (
        <>
          <SmartDateInput source="from" label="From" validate={[required(), dateValidation, rangeValidation]} helperText={false} />
          <SmartDateInput source="to" label="To" validate={[required(), dateValidation, rangeValidation]} helperText={false} />
        </>
      )}
    </>
  );
};

export default DateRange;
